import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  cn,
} from "@musa.tech/design-system";

interface FilterDropdownButtonProps {
  label: string;
  /** Quantos valores estão selecionados — vira sufixo no rótulo. */
  count?: number;
  /** Substitui o rótulo quando há um valor único a exibir (ex.: período). */
  valueLabel?: string;
  isLoading?: boolean;
  disabled?: boolean;
  /** Avisa abertura e fechamento — usado para limpar a busca ao fechar. */
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
  contentClassName?: string;
}

/**
 * Botão branco de 32px que abre um popover — base dos filtros do dashboard.
 * Visual do Figma 124:12961 (o `font-inter` sobrepõe o `font-sora` do Button
 * do DS, porque o design usa Inter nos filtros).
 */
export const FilterDropdownButton = ({
  label,
  count = 0,
  valueLabel,
  isLoading = false,
  disabled = false,
  onOpenChange,
  children,
  contentClassName,
}: FilterDropdownButtonProps) => {
  const text = valueLabel ?? (count > 0 ? `${label} (${count})` : label);

  return (
    <Popover onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          disabled={disabled || isLoading}
          aria-label={`Filtrar por ${label}`}
          className={cn(
            "h-8 gap-2 rounded-lg bg-white px-4 py-2 font-inter",
            "text-sm font-semibold leading-4 text-neutral-800",
            "hover:bg-neutral-50 hover:text-neutral-900",
          )}
        >
          {isLoading ? (
            <Skeleton className="h-4 w-20" />
          ) : (
            <span className="truncate">{text}</span>
          )}
          <ChevronDown className="size-3 shrink-0" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn("w-[260px] max-w-[95vw] p-0", contentClassName)}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
};
